import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { rateCards } from "../services/api";
import type { AnyHaulierRate, Haulier, RateCard, WarehouseRate } from "../types";
import { useAuth } from "./useAuth";

/**
 * TanStack Query hook for fetching all rate cards.
 */
export function useRateCards() {
  return useQuery<{ rate_cards: RateCard[] }>({
    queryKey: ["rateCards"],
    queryFn: () => rateCards.list(),
  });
}

/**
 * TanStack Query hook for fetching the individual rate rows of a rate card
 * (Box bands, TPN districts, etc). Disabled until a card id is available.
 */
export function useRateCardRates(rateCardId: string | null | undefined) {
  return useQuery<{ rates: (AnyHaulierRate | WarehouseRate)[] }>({
    queryKey: ["rateCardRates", rateCardId],
    queryFn: () => rateCards.getRates(rateCardId as string),
    enabled: !!rateCardId,
  });
}

/**
 * Mutation for uploading a new haulier rate card CSV.
 * Handles: get upload URL -> upload to S3 -> register rate card.
 */
export function useUploadHaulierRateCard() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      file,
      effectiveFrom,
      haulier,
    }: {
      file: File;
      effectiveFrom: string;
      haulier: Haulier;
    }) => {
      // Read CSV content from the file
      const csvContent = await file.text();

      // Send CSV content directly to the backend
      return rateCards.uploadHaulier(
        csvContent,
        effectiveFrom,
        user?.email ?? "unknown",
        haulier,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rateCards"] });
    },
  });
}

/**
 * Mutation for setting warehouse rates.
 */
export function useSetWarehouseRates() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      rates,
      effectiveFrom,
    }: {
      rates: { charge_type: string; unit_price: number }[];
      effectiveFrom: string;
    }) => {
      return rateCards.setWarehouse(
        rates,
        effectiveFrom,
        user?.email ?? "unknown",
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rateCards"] });
    },
  });
}
